/**
 * Tracking Service
 * Dynamically loads and initializes Google Analytics (gtag) and Facebook Pixel
 */

let gtagInitialized = false;
let fbPixelInitialized = false;
let gtagId = null;
let fbPixelId = null;
let googleAdsConversionId = null;
let googleAdsConversionLabel = null;

/**
 * Initializes Google Analytics (gtag.js)
 * @param {string} measurementId - Google Analytics Measurement ID (G-XXXXXXXXXX)
 */
export const initializeGoogleAnalytics = (measurementId) => {
  if (gtagInitialized || !measurementId) {
    console.log('Google Analytics already initialized or no measurement ID provided');
    return;
  }

  try {
    // Create and append the gtag script
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(gtagScript);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    
    gtag('js', new Date());
    gtag('config', measurementId);

    gtagInitialized = true;
    gtagId = measurementId;
    console.log('Google Analytics initialized with ID:', measurementId);
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Initializes Facebook Pixel
 * @param {string} pixelId - Facebook Pixel ID
 */
export const initializeFacebookPixel = (pixelId) => {
  if (!pixelId) {
    console.log('Facebook Pixel: No pixel ID provided');
    return;
  }

  // If already initialized with the same pixel ID, skip
  if (fbPixelInitialized && fbPixelId === pixelId) {
    console.log('Facebook Pixel already initialized with ID:', pixelId);
    return;
  }

  // If initialized with a different pixel ID, log a warning
  // (Facebook Pixel doesn't easily support multiple pixels, so we'll use the first one)
  if (fbPixelInitialized && fbPixelId !== pixelId) {
    console.warn(`Facebook Pixel already initialized with ID ${fbPixelId}. Cannot switch to ${pixelId}.`);
    return;
  }

  try {
    // Check if pixel script is already in the head (for Meta Pixel Helper detection)
    let pixelScript = document.querySelector('script[src*="connect.facebook.net/en_US/fbevents.js"]');
    
    if (!pixelScript) {
      // Facebook Pixel Code - Initialize fbq function first
      !function(f,b,e,v,n,t,s) {
        if(f.fbq) return;
        n=f.fbq=function(){
          n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments)
        };
        if(!f._fbq) f._fbq=n;
        n.push=n;
        n.loaded=!0;
        n.version='2.0';
        n.queue=[];
        t=b.createElement(e);
        t.async=!0;
        t.src=v;
        s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)
      }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
    }

    // Initialize pixel - ensure script is loaded before calling init/track
    const initPixel = () => {
      if (typeof window.fbq === 'function') {
        try {
          // Clear any existing initialization
          if (fbPixelInitialized && fbPixelId !== pixelId) {
            console.warn(`Reinitializing Facebook Pixel: ${fbPixelId} -> ${pixelId}`);
          }
          
          window.fbq('init', pixelId);
          window.fbq('track', 'PageView');
          
          // Verify initialization
          console.log('✅ Facebook Pixel initialized with ID:', pixelId);
          console.log('✅ Facebook Pixel PageView event fired');
          
          // Log pixel status for debugging
          if (window.fbq.queue) {
            console.log('Facebook Pixel queue length:', window.fbq.queue.length);
          }
          
          fbPixelInitialized = true;
          fbPixelId = pixelId;
        } catch (err) {
          console.error('❌ Error initializing Facebook Pixel:', err);
        }
      } else {
        // If fbq isn't ready yet, wait a bit and try again (max 2 seconds)
        const maxAttempts = 20;
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          if (typeof window.fbq === 'function') {
            clearInterval(checkInterval);
            initPixel();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            console.error('❌ Facebook Pixel script failed to load after 2 seconds');
          }
        }, 100);
      }
    };

    // Start initialization immediately
    initPixel();
  } catch (error) {
    console.error('❌ Failed to initialize Facebook Pixel:', error);
  }
};

/**
 * Initializes Google Ads Conversion Tracking
 * @param {string} conversionId - Google Ads Conversion ID (AW-XXXXXXXXXX)
 * @param {string} conversionLabel - Google Ads Conversion Label
 */
export const initializeGoogleAdsConversion = (conversionId, conversionLabel) => {
  if (!conversionId || !conversionLabel) {
    console.log('Google Ads conversion tracking requires both conversion ID and label');
    return;
  }

  try {
    // Load gtag.js script if not already loaded
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
      document.head.appendChild(gtagScript);
    }

    // Initialize gtag if not already done
    if (!gtagInitialized) {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag('js', new Date());
      gtagInitialized = true;
    }

    // Configure Google Ads conversion
    gtag('config', conversionId, {
      'send_page_view': false
    });

    googleAdsConversionId = conversionId;
    googleAdsConversionLabel = conversionLabel;
    console.log('Google Ads conversion tracking initialized:', { conversionId, conversionLabel });
  } catch (error) {
    console.error('Failed to initialize Google Ads conversion tracking:', error);
  }
};

/**
 * Tracks a page view (Google Analytics)
 * @param {string} url - Page URL
 * @param {string} title - Page title
 */
export const trackPageView = (url, title) => {
  if (gtagInitialized && window.gtag) {
    window.gtag('config', gtagId, {
      page_path: url,
      page_title: title
    });
  }
};

/**
 * Tracks a custom event (Google Analytics)
 * @param {string} eventName - Event name
 * @param {Object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Google Analytics
  if (gtagInitialized && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }

  // Facebook Pixel
  if (fbPixelInitialized && window.fbq) {
    window.fbq('trackCustom', eventName, eventParams);
  }
};

/**
 * Tracks form submission
 * @param {string} formName - Name of the form
 * @param {Object} formData - Form data (will be sanitized)
 */
export const trackFormSubmission = (formName, formData = {}) => {
  const sanitizedData = {
    form_name: formName,
    ...formData
  };

  trackEvent('form_submission', sanitizedData);
  
  // Facebook Pixel Lead event
  if (fbPixelInitialized && window.fbq) {
    window.fbq('track', 'Lead', { content_name: formName });
  }
};

/**
 * Tracks button click
 * @param {string} buttonName - Button identifier
 * @param {Object} additionalData - Additional event data
 */
export const trackButtonClick = (buttonName, additionalData = {}) => {
  trackEvent('button_click', {
    button_name: buttonName,
    ...additionalData
  });
};

/**
 * Tracks conversion/purchase
 * @param {number} value - Conversion value
 * @param {string} currency - Currency code (default: USD)
 * @param {Object} additionalData - Additional conversion data
 */
export const trackConversion = (value, currency = 'USD', additionalData = {}) => {
  // Google Analytics
  if (gtagInitialized && window.gtag) {
    window.gtag('event', 'conversion', {
      value: value,
      currency: currency,
      ...additionalData
    });
  }

  // Google Ads Conversion
  if (googleAdsConversionId && googleAdsConversionLabel && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': `${googleAdsConversionId}/${googleAdsConversionLabel}`
    });
  }

  // Facebook Pixel
  if (fbPixelInitialized && window.fbq) {
    window.fbq('track', 'Purchase', {
      value: value,
      currency: currency,
      ...additionalData
    });
  }
};

/**
 * Tracks when user views content
 * @param {string} contentName - Name/ID of the content
 * @param {string} contentType - Type of content
 */
export const trackContentView = (contentName, contentType) => {
  trackEvent('content_view', {
    content_name: contentName,
    content_type: contentType
  });

  // Facebook Pixel
  if (fbPixelInitialized && window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_name: contentName,
      content_type: contentType
    });
  }
};

/**
 * Checks if tracking is initialized
 * @returns {Object} Status of tracking services
 */
export const getTrackingStatus = () => {
  return {
    googleAnalytics: {
      initialized: gtagInitialized,
      id: gtagId
    },
    googleAdsConversion: {
      initialized: !!(googleAdsConversionId && googleAdsConversionLabel),
      conversionId: googleAdsConversionId,
      conversionLabel: googleAdsConversionLabel
    },
    facebookPixel: {
      initialized: fbPixelInitialized,
      id: fbPixelId
    }
  };
};

